import {useFormik} from 'formik';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import * as Yup from 'yup';
import {isValidPhoneNumber} from 'react-phone-number-input';
import {HttpError} from '../../../../common/hooks/ApiClient';
import {useAuthedMutation} from '../../../../common/hooks/QueryHooks';
import {useI18n} from '../../../../common/providers/I18nProvider';
import {routes} from '../../../../routing/routes';
import {Enterprise} from '../../common/Types';
import {useAuth} from '../../providers/AuthProvider';
import {Geo} from './Types';

interface Values {
	companyName?: string;
	storeName?: string;
	managerName?: string;
	businessEmail?: string;
	location?: string;
	zipCode?: string;
	prefecture?: string;
	streetNo?: string;
	buildingName?: string;
	nearestStation?: string;
}

interface CompleteProfileResponse {
	message: string;
	enterpriser: Enterprise;
}

const useProfile = () => {
	const [loading, setLoading] = useState(false);
	const [phone, setPhone] = useState('');
	const [geo, setGeo] = useState<Geo | undefined>(undefined);
	const {mutate} = useAuthedMutation<CompleteProfileResponse>('/enterpriser/profile/complete-profile');
	const navigate = useNavigate();
	const {updateEnterpriseInfo} = useAuth();

	const {i18n} = useI18n();
	const profileSchema = Yup.object().shape({
		companyName: Yup.string()
			.min(1, i18n('AUTH.PROFILE.VALIDATION.COMPANY.MIN', {val: 1}))
			.max(50, i18n('AUTH.PROFILE.VALIDATION.COMPANY.MAX', {val: 50}))
			.required(i18n('AUTH.PROFILE.VALIDATION.COMPANY.REQUIRED')),
		managerName: Yup.string()
			.min(1, i18n('AUTH.PROFILE.VALIDATION.MANAGER_NAME.MIN', {val: 1}))
			.max(50, i18n('AUTH.PROFILE.VALIDATION.MANAGER_NAME.MAX', {val: 50}))
			.required(i18n('AUTH.PROFILE.VALIDATION.MANAGER_NAME.REQUIRED')),
		businessEmail: Yup.string()
			.min(3, i18n('AUTH.PROFILE.VALIDATION.BUSINESS_EMAIL.MIN', {val: 3}))
			.max(50, i18n('AUTH.PROFILE.VALIDATION.BUSINESS_EMAIL.MAX', {val: 50}))
			.required(i18n('AUTH.PROFILE.VALIDATION.BUSINESS_EMAIL.REQUIRED'))
			.test('email', `${i18n('AUTH.PROFILE.VALIDATION.EMAIL.INVALID')}`, (value) => {
				const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
				return emailRegex.test(value);
			}),
		storeName: Yup.string()
			.min(1, i18n('AUTH.PROFILE.VALIDATION.STORE.MIN', {val: 1}))
			.max(50, i18n('AUTH.PROFILE.VALIDATION.STORE.MAX', {val: 50}))
			.required(i18n('AUTH.PROFILE.VALIDATION.STORE.REQUIRED')),
		location: Yup.string().required(i18n('AUTH.PROFILE.VALIDATION.LOCATION.REQUIRED')),
		zipCode: Yup.string()
			.min(1, i18n('AUTH.PROFILE.VALIDATION.ZIP.MIN', {val: 1}))
			.max(50, i18n('AUTH.PROFILE.VALIDATION.ZIP.MAX', {val: 50}))
			.required(i18n('AUTH.PROFILE.VALIDATION.ZIP.REQUIRED')),
		prefecture: Yup.string()
			.min(1, i18n('AUTH.PROFILE.VALIDATION.PREFECTURE.MIN', {val: 1}))
			.max(50, i18n('AUTH.PROFILE.VALIDATION.PREFECTURE.MAX', {val: 50}))
			.required(i18n('AUTH.PROFILE.VALIDATION.PREFECTURE.REQUIRED')),
		streetNo: Yup.string()
			.min(1, i18n('AUTH.PROFILE.VALIDATION.STREET.MIN', {val: 1}))
			.max(50, i18n('AUTH.PROFILE.VALIDATION.STREET.MAX', {val: 50}))
			.required(i18n('AUTH.PROFILE.VALIDATION.STREET.REQUIRED')),
		buildingName: Yup.string()
			.min(1, i18n('AUTH.PROFILE.VALIDATION.BUILDING.MIN', {val: 1}))
			.max(50, i18n('AUTH.PROFILE.VALIDATION.BUILDING.MAX', {val: 50}))
			.required(i18n('AUTH.PROFILE.VALIDATION.BUILDING.REQUIRED')),
		nearestStation: Yup.string()
			.min(1, i18n('AUTH.PROFILE.VALIDATION.STATION.MIN', {val: 1}))
			.max(50, i18n('AUTH.PROFILE.VALIDATION.STATION.MAX', {val: 50})),
	});

	const buildApiData = (values: Values) => {
		return {
			storeName: values.storeName,
			managerName: values.managerName,
			businessEmail: values.businessEmail,
			storePhone: phone,
			prefecture: values.prefecture,
			streetNumber: values.streetNo,
			buildingName: values.buildingName,
			nearestStation: values.nearestStation,
			zipCode: values.zipCode,
			companyName: values.companyName,
			mapLocation: {
				address: values.location,
				lat: geo?.lat,
				lng: geo?.lng,
			},
		};
	};

	const formik = useFormik({
		initialValues: {
			companyName: undefined,
			storeName: undefined,
			managerName: undefined,
			businessEmail: undefined,
			location: undefined,
			zipCode: undefined,
			prefecture: undefined,
			streetNo: undefined,
			buildingName: undefined,
			nearestStation: undefined,
		},
		validationSchema: profileSchema,
		onSubmit: async (values, {setStatus, setSubmitting}) => {
			setLoading(true);
			try {
				const data = buildApiData(values);
				const completedEnterprise = (await mutate({data})).enterpriser;
				completedEnterprise.profileCompleted = true;
				completedEnterprise.verified = true;
				updateEnterpriseInfo(completedEnterprise);
				navigate(routes.dashboard);
			} catch (error) {
				if (error instanceof HttpError) {
					setStatus(error.message);
				}
			} finally {
				setSubmitting(false);
				setLoading(false);
			}
		},
	});

	const isValidPhone = phone === '' || (isValidPhoneNumber(phone, 'JP') && /^\+810[789]0\d{7,8}$/.test(phone));
	// const isValidPhone = phone === '' || isValidPhoneNumber(phone, 'JP');

	const phoneError = isValidPhone ? undefined : i18n('AUTH.PROFILE.VALIDATION.PHONE.INVALID');
	const isInvalid = {
		companyName: formik.touched.companyName && formik.errors.companyName,
		managerName: formik.touched.managerName && formik.errors.managerName,
		businessEmail: formik.touched.businessEmail && formik.errors.businessEmail,
		storeName: formik.touched.storeName && formik.errors.storeName,
		// location: formik.touched.location && (formik.errors.location || geo === undefined),
		location: formik.touched.location && !formik.values.location,
		zipCode: formik.touched.zipCode && formik.errors.zipCode,
		prefecture: formik.touched.prefecture && formik.errors.prefecture,
		streetNo: formik.touched.streetNo && formik.errors.streetNo,
		buildingName: formik.touched.buildingName && formik.errors.buildingName,
		nearestStation: formik.touched.nearestStation && formik.errors.nearestStation,
		storePhone: phoneError !== undefined,
	};

	const canNotSubmit =
		formik.isSubmitting ||
		!formik.isValid ||
		!formik.touched.storeName ||
		geo === undefined ||
		phone === '' ||
		phoneError !== undefined;

	return {loading, formik, canNotSubmit, isInvalid, phoneError, setPhone, setGeo, geo};
};

export default useProfile;
